import type { DefectMark } from '../components/DefectMarkPad'

type DefectMarkLike = {
  id?: string
  displayIndex?: number
  x: number
  y: number
  label: string
  photo_id?: string
  photo_index?: number
  part?: DefectMark['part']
  type?: DefectMark['type']
  severity?: DefectMark['severity']
  description?: string
}

export function newDefectId(): string {
  return `df_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 9)}`
}

export function buildDefectMarkLabel(m: Pick<DefectMark, 'part' | 'type' | 'severity' | 'description' | 'label'>): string {
  const desc = m.description?.trim()
  if (desc) return desc.slice(0, 200)
  const chunks: string[] = []
  if (m.part === 'screen') chunks.push('Screen')
  else if (m.part === 'frame') chunks.push('Frame / body')
  else if (m.part === 'back_glass') chunks.push('Back glass')
  else if (m.part === 'camera') chunks.push('Camera')
  else if (m.part === 'other') chunks.push('Other area')
  if (m.type === 'scratch') chunks.push('Scratch')
  else if (m.type === 'dent') chunks.push('Dent')
  else if (m.type === 'crack') chunks.push('Crack')
  else if (m.type === 'wear') chunks.push('Wear')
  else if (m.type === 'other') chunks.push('Other defect')
  if (m.severity === 'minor') chunks.push('Minor')
  else if (m.severity === 'visible') chunks.push('Visible')
  else if (m.severity === 'significant') chunks.push('Significant')
  if (chunks.length > 0) return chunks.join(' · ')
  return m.label?.trim() || 'Defect'
}

/** Assign ids + global displayIndex to legacy marks (stable creation order). */
export function migrateDefectMarks(marks: DefectMark[], photos: { id: string }[]): DefectMark[] {
  const withPhoto = normalizeDefectMarks(marks, photos)
  const ordered = orderDefectMarks(withPhoto, photos)
  return ordered.map((m, i) => ({
    ...m,
    id: m.id ?? newDefectId(),
    displayIndex: m.displayIndex ?? i + 1,
    label: m.label || buildDefectMarkLabel(m),
  }))
}

export function nextDefectDisplayIndex(marks: DefectMark[]): number {
  if (marks.length === 0) return 1
  return Math.max(...marks.map((m) => m.displayIndex ?? 0)) + 1
}

export function renumberDefectDisplayIndices(marks: DefectMark[]): DefectMark[] {
  const sorted = [...marks].sort((a, b) => (a.displayIndex ?? 9999) - (b.displayIndex ?? 9999))
  return sorted.map((m, i) => ({ ...m, displayIndex: i + 1 }))
}

export function createDefectMark(
  marks: DefectMark[],
  draft: {
    photo_id: string
    x: number
    y: number
    part?: DefectMark['part']
    type?: DefectMark['type']
    severity?: DefectMark['severity']
    description?: string
  },
): DefectMark[] {
  const displayIndex = nextDefectDisplayIndex(marks)
  const base: DefectMark = {
    id: newDefectId(),
    displayIndex,
    photo_id: draft.photo_id,
    x: draft.x,
    y: draft.y,
    part: draft.part ?? 'frame',
    type: draft.type ?? 'scratch',
    severity: draft.severity ?? 'minor',
    description: draft.description ?? '',
    label: '',
  }
  base.label = buildDefectMarkLabel(base)
  return [...marks, base]
}

export function updateDefectMark(marks: DefectMark[], id: string, patch: Partial<DefectMark>): DefectMark[] {
  return marks.map((m) => {
    if (m.id !== id) return m
    const next = { ...m, ...patch, id: m.id }
    next.label = buildDefectMarkLabel(next)
    return next
  })
}

export function deleteDefectMark(marks: DefectMark[], id: string): DefectMark[] {
  return renumberDefectDisplayIndices(marks.filter((m) => m.id !== id))
}

export function sortDefectMarksByDisplayIndex<T extends DefectMarkLike>(marks: T[]): T[] {
  return [...marks].sort((a, b) => {
    const ai = a.displayIndex
    const bi = b.displayIndex
    if (ai != null && bi != null) return ai - bi
    if (ai != null) return -1
    if (bi != null) return 1
    return 0
  })
}

function photoOrderForMark(m: DefectMarkLike, photos?: { id: string }[]): number {
  if (m.photo_index != null) return m.photo_index
  if (m.photo_id && photos) {
    const idx = photos.findIndex((p) => p.id === m.photo_id)
    return idx >= 0 ? idx : 999
  }
  return 0
}

function defectMarkKey(m: DefectMarkLike, photos?: { id: string }[]): string {
  return `${photoOrderForMark(m, photos)}:${m.x}:${m.y}:${m.label}`
}

/** Global defect order: earlier photos first, then mark order within each photo. */
export function orderDefectMarks<T extends DefectMarkLike>(
  marks: T[],
  photos?: { id: string }[],
): T[] {
  return marks
    .map((m, i) => ({ m, i }))
    .sort((a, b) => {
      const photoDiff = photoOrderForMark(a.m, photos) - photoOrderForMark(b.m, photos)
      if (photoDiff !== 0) return photoDiff
      return a.i - b.i
    })
    .map(({ m }) => m)
}

/** 1-based defect number across all photos (not per photo). */
export function globalDefectNumber<T extends DefectMarkLike>(
  allMarks: T[],
  mark: T,
  photos?: { id: string }[],
): number {
  if (mark.displayIndex != null && mark.displayIndex > 0) return mark.displayIndex
  const ordered = orderDefectMarks(allMarks, photos)
  const key = defectMarkKey(mark, photos)
  const idx = ordered.findIndex((m) => defectMarkKey(m, photos) === key)
  return idx >= 0 ? idx + 1 : 0
}

export function newPhotoId(): string {
  return `ph_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`
}

export function marksForPhotoId(marks: DefectMark[], photoId: string): DefectMark[] {
  return marks.filter((m) => m.photo_id === photoId)
}

/** Assign orphan marks (no photo_id) to the first photo so they stay visible per-photo. */
export function normalizeDefectMarks(marks: DefectMark[], photos: { id: string }[]): DefectMark[] {
  if (photos.length === 0) return marks
  const fallbackId = photos[0].id
  return marks.map((m) => (m.photo_id ? m : { ...m, photo_id: fallbackId }))
}

export function setMarksForPhotoId(
  marks: DefectMark[],
  photoId: string,
  next: DefectMark[],
): DefectMark[] {
  return [
    ...marks.filter((m) => m.photo_id !== photoId),
    ...next.map((m) => ({ ...m, photo_id: photoId })),
  ]
}

export function removeMarksForPhotoId(marks: DefectMark[], photoId: string): DefectMark[] {
  return marks.filter((m) => m.photo_id !== photoId)
}

export function defectMarksForSubmit(
  marks: DefectMark[],
  photos: { id: string }[],
): DefectMark[] {
  const ready = migrateDefectMarks(marks, photos)
  return sortDefectMarksByDisplayIndex(ready)
    .filter((m) => m.photo_id && photos.some((p) => p.id === m.photo_id))
    .map((m) => ({
      x: m.x,
      y: m.y,
      label: buildDefectMarkLabel(m),
      photo_index: photos.findIndex((p) => p.id === m.photo_id),
    }))
    .filter((m) => m.photo_index >= 0)
}

export function formatDefectDescription(
  marks: DefectMark[],
  photos: { id: string }[],
): string {
  if (marks.length === 0) return ''
  const ready = migrateDefectMarks(marks, photos)
  const lines = sortDefectMarksByDisplayIndex(ready).map((m, i) => {
    const n = m.displayIndex ?? globalDefectNumber(ready, m, photos)
    const photoNo =
      m.photo_index != null
        ? m.photo_index + 1
        : m.photo_id
          ? photos.findIndex((p) => p.id === m.photo_id) + 1
          : 1
    const label = buildDefectMarkLabel(m)
    return `${n}. ${photoNo > 0 ? photoNo : i + 1} ${label}`
  })
  return `Marked defects (${marks.length}):\n${lines.join('\n')}`
}

const DEFECT_DESC_HEADER_LT = /^Pažymėti defektai \(\d+\):/
const DEFECT_DESC_HEADER_EN = /^Marked defects \(\d+\):/

export type ParsedListingDefectMark = {
  x: number
  y: number
  label: string
  photo_index?: number
}

/** True when description is only the auto-generated defect list (no seller prose). */
export function isDefectOnlyDescription(description: string | null | undefined): boolean {
  if (!description?.trim()) return false
  const trimmed = description.trim()
  return DEFECT_DESC_HEADER_LT.test(trimmed) || DEFECT_DESC_HEADER_EN.test(trimmed)
}

/** Seller-written text for “Aprašymas”; hides auto-generated defect blob. */
export function sellerDescriptionExcludingDefects(description: string | null | undefined): string {
  if (!description?.trim()) return ''
  if (isDefectOnlyDescription(description)) return ''
  return description.trim()
}

/** Fallback when API has description blob but empty defect_marks. */
export function parseDefectMarksFromDescription(
  description: string | null | undefined,
): ParsedListingDefectMark[] {
  if (!isDefectOnlyDescription(description)) return []
  const trimmed = description!.trim()
  const body = trimmed
    .replace(DEFECT_DESC_HEADER_LT, '')
    .replace(DEFECT_DESC_HEADER_EN, '')
    .replace(/^\s*\n?/, '')
  const marks: ParsedListingDefectMark[] = []
  for (const line of body.split('\n')) {
    const trimmedLine = line.trim()
    if (!trimmedLine) continue
    const compact = trimmedLine.match(/^\d+\.\s*(\d+)\s+(.+)$/)
    if (compact) {
      const photoNo = parseInt(compact[1], 10)
      marks.push({
        x: 0,
        y: 0,
        label: compact[2].trim(),
        photo_index: photoNo > 0 ? photoNo - 1 : 0,
      })
      continue
    }
    const m = trimmedLine.match(/^\d+\.\s*(?:(?:Nuotr\.|Photo)\s*(\d+):\s*)?(.+)$/i)
    if (!m) continue
    const photoNo = m[1] ? parseInt(m[1], 10) : 0
    marks.push({
      x: 0,
      y: 0,
      label: m[2].trim(),
      photo_index: photoNo > 0 ? photoNo - 1 : 0,
    })
  }
  return marks
}

export function resolveListingDefectMarks(
  marks: ParsedListingDefectMark[] | null | undefined,
  description: string | null | undefined,
): ParsedListingDefectMark[] {
  if (marks?.length) return marks
  return parseDefectMarksFromDescription(description)
}
